import React, { createContext, useContext, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  MutationEndVideoSessionArgs,
  MutationResult,
  MutationStartVideoSessionArgs,
  QueryJoinVideoSessionArgs,
  QueryResult,
} from '@/shared/types/generated';
import { END_VIDEO_SESSION, START_VIDEO_SESSION } from '@/shared/mutations';
import { JOIN_VIDEO_SESSION } from '@/shared/queries';
import useStore from '@/stores/useStore';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useShallow } from 'zustand/react/shallow';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';
import SentryService from '@/services/sentry/Sentry.service';
import Video, { Room } from 'twilio-video';
import { USE_TWILIO_VIDEO } from '@/shared/constants/flags';

interface TwilioContextType {
  // We use any type here because the ref type comes from the Mobile SDK. It's not used
  // on web, but we want to keep the context type consistent across both platforms.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  twilioRef: any;
  room: Room | null;
  startSession: (guestId: number, consultationId: number) => Promise<void>;
  joinSession: (sessionName: string) => Promise<void>;
  endSession: (sessionName?: string) => Promise<void>;
  sessionStartTime?: number;
  leaveSession: () => Promise<void>;
  pendingConnection: { token: string; sessionName: string } | null;
  clearPendingConnection: () => void;
}

const TwilioContext = createContext<TwilioContextType | undefined>(undefined);

export const TwilioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { toggleInstantStyling } = useInstantStylingToggle();

  // Fetch channel URL for chat during session
  const fetchAndSetChannelUrl = async (
    hostId: number | undefined | null,
    guestId: number | undefined | null,
    errorContext: string,
  ) => {
    try {
      if (hostId && guestId) {
        const channelUrl = await fetchChannelUrl(hostId, guestId);
        setSessionChannelUrl(channelUrl);
      } else {
        setSessionChannelUrl(undefined);
        throw new Error('Missing host or guest ID for chat URL fetch');
      }
    } catch (error) {
      SentryService.captureError(errorContext, error);
    }
  };

  const {
    activeSessionName,
    setActiveSessionName,
    setIsInSession,
    setCompletedSessionDuration,
    setSessionChannelUrl,
    setEstimatedSessionDuration,
    setCompletedConsultationId,
  } = useStore(
    useShallow((state) => ({
      activeSessionName: state.activeSessionName,
      setActiveSessionName: state.setActiveSessionName,
      setIsInSession: state.setIsInSession,
      setCompletedSessionDuration: state.setCompletedSessionDuration,
      setSessionChannelUrl: state.setSessionChannelUrl,
      setEstimatedSessionDuration: state.setEstimatedSessionDuration,
      setCompletedConsultationId: state.setCompletedConsultationId,
    })),
  );

  const { isInstantStylingActive, resetClientAndSharedState } =
    useInstantStylingStore(
      useShallow((state) => ({
        isInstantStylingActive: state.isInstantStylingActive,
        resetClientAndSharedState: state.resetClientAndSharedState,
      })),
    );

  const [room, setRoom] = useState<Room | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number>();

  const [startVideoSession] = useMutation<
    MutationResult<'startVideoSession'>,
    MutationStartVideoSessionArgs
  >(START_VIDEO_SESSION);

  const [joinVideoSession] = useLazyQuery<
    QueryResult<'joinVideoSession'>,
    QueryJoinVideoSessionArgs
  >(JOIN_VIDEO_SESSION);

  const [endVideoSession] = useMutation<
    MutationResult<'endVideoSession'>,
    MutationEndVideoSessionArgs
  >(END_VIDEO_SESSION);

  const startSession = async (guestId: number, consultationId: number) => {
    if (!USE_TWILIO_VIDEO) {
      return;
    }

    const { data } = await startVideoSession({
      variables: {
        input: {
          guestId,
          consultationId,
        },
      },
    });

    const sessionName = data?.startVideoSession?.session.sessionName;
    const token = data?.startVideoSession?.twilioToken;
    const startedAt = data?.startVideoSession?.session.startedAt;
    const hostId = data?.startVideoSession?.session.hostId;

    if (!sessionName || !token) {
      throw new Error('Session name or token not found');
    }

    setActiveSessionName(sessionName);
    setSessionStartTime(startedAt);

    await fetchAndSetChannelUrl(
      hostId,
      guestId,
      'Failed to fetch channel url during session start',
    );

    if (isInstantStylingActive) {
      await toggleInstantStyling();
    }

    try {
      const connectedRoom = await Video.connect(token, {
        name: sessionName,
        audio: true,
        video: { width: 1280, height: 720, aspectRatio: 16 / 9 },
      });

      __DEV__ &&
        console.log('Twilio room connected (start):', {
          name: connectedRoom.name,
          participants: connectedRoom.participants.size,
        });

      setRoom(connectedRoom);
      setIsInSession(true);
    } catch (error) {
      SentryService.captureError('Failed to connect to Twilio room', error);
      throw error;
    }
  };

  const joinSession = async (sessionName: string) => {
    if (!USE_TWILIO_VIDEO) {
      return;
    }

    const { data } = await joinVideoSession({
      variables: {
        input: {
          sessionName,
        },
      },
    });

    const token = data?.joinVideoSession?.twilioToken;
    const startedAt = data?.joinVideoSession?.session.startedAt;
    const hostId = data?.joinVideoSession?.session.hostId;
    const guestId = data?.joinVideoSession?.session.guestId;

    if (!token) {
      throw new Error('Twilio token not found');
    }

    setActiveSessionName(sessionName);
    setSessionStartTime(startedAt);

    await fetchAndSetChannelUrl(
      hostId,
      guestId,
      'Failed to fetch channel url during session join',
    );

    if (isInstantStylingActive) {
      await toggleInstantStyling();
    }

    try {
      const connectedRoom = await Video.connect(token, {
        name: sessionName,
        audio: true,
        video: { width: 1280, height: 720, aspectRatio: 16 / 9 },
      });

      __DEV__ &&
        console.log('Twilio room connected (join):', {
          name: connectedRoom.name,
          participants: connectedRoom.participants.size,
        });

      setRoom(connectedRoom);
      setIsInSession(true);
    } catch (error) {
      SentryService.captureError('Failed to connect to Twilio room', error);
      throw error;
    }
  };

  const leaveSession = async () => {
    if (!USE_TWILIO_VIDEO) {
      return;
    }

    if (room) {
      // Stop all local tracks to release camera/microphone
      room.localParticipant.tracks.forEach((publication) => {
        const track = publication.track;
        if (track && track.kind !== 'data') {
          track.stop();
        }
      });

      room.disconnect();
      setRoom(null);
    }
  };

  const endSession = async (sessionName?: string) => {
    if (!USE_TWILIO_VIDEO) {
      return;
    }

    const { data: sessionData } = await endVideoSession({
      variables: {
        input: {
          sessionName: sessionName || activeSessionName,
        },
      },
    });

    const duration = sessionData?.endVideoSession?.durationSeconds;
    setCompletedSessionDuration(duration);

    __DEV__ &&
      console.log('Ending Twilio session:', {
        sessionName: sessionName || activeSessionName,
        duration,
      });

    if (room) {
      // Stop all local tracks to release camera/microphone
      room.localParticipant.tracks.forEach((publication) => {
        const track = publication.track;
        if (track && track.kind !== 'data') {
          track.stop();
        }
      });

      room.disconnect();
      setRoom(null);
    }

    const consultation = sessionData?.endVideoSession?.session.consultation;
    const estimatedDuration = consultation?.estimatedDuration;
    const hostId = sessionData?.endVideoSession?.session.hostId;
    const guestId = sessionData?.endVideoSession?.session.guestId;

    setEstimatedSessionDuration(estimatedDuration);
    setCompletedConsultationId(consultation?.id);

    await fetchAndSetChannelUrl(
      hostId,
      guestId,
      'Failed to fetch channel url during session end',
    );

    resetClientAndSharedState();
  };

  const clearPendingConnection = () => {
    // No-op on web - pendingConnection not used
  };

  return (
    <TwilioContext.Provider
      value={{
        twilioRef: null,
        room,
        startSession,
        joinSession,
        endSession,
        sessionStartTime,
        leaveSession,
        pendingConnection: null, // Not used on web
        clearPendingConnection,
      }}
    >
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilio = (): TwilioContextType => {
  const context = useContext(TwilioContext);
  if (!context) {
    throw new Error('useTwilio must be used within a TwilioProvider');
  }
  return context;
};
